// ── CloudHub Enterprise Redesign ────────────────────────────────────────────
// Flagship case study rendered as a five-chapter narrative. Tightened for
// subtlety, fewer hypothetical quotes, fewer redundant sub-sections, and
// meaningful outcome numbers only.

export const cloudhubEnterpriseRedesign = {
    slug: 'cloudhub-enterprise-redesign',
    no: '03',
    type: 'rich',
    title: 'CloudHub Enterprise Redesign',
    subtitle: 'From internal ops tool to scalable multi-tenant SaaS',
    category: 'Enterprise SaaS',
    sourceUrl: 'https://himanshutanty.com/portfolio/cloudhub-enterprise-redesign/',
    cover: '/api/files/150768d7-29f4-44c7-b1f7-0eb28d06975d',
    client: 'Confidential · Enterprise SaaS',
    timeline: '3 months · Lead Product Designer',
    responsibilities: [
        'User research & discovery',
        'Information architecture',
        'Interaction design & flows',
        'Design system & tokens',
        'Visual design across 5 modules',
    ],
    overview:
        'Cloud Hub started as an internal cloud-management tool. The project turned it into a multi-tenant SaaS product businesses of any size could adopt, with a clearer interface, streamlined workflows and a coherent visual system for hybrid multi-cloud operations.',
    blocks: [
        {
            kind: 'tabs',
            tabs: [
                // ═══════════════════════════════════════════════════════
                // 01 · OVERVIEW
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Overview',
                    summary:
                        'Turning an internal cloud-ops tool into a scalable, multi-tenant SaaS product teams of any size can adopt.',
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'A platform, not a tool',
                            paragraph:
                                'CloudHub was an internal ops tool that quietly ran critical infrastructure inside the org. The redesign turned it into a multi-tenant SaaS product, the shift changed positioning, permissions, onboarding and the shape of the product itself.',
                        },
                        {
                            kind: 'meta-list',
                            items: [
                                { label: 'Role', value: 'Lead Product Designer' },
                                { label: 'Scope', value: 'Research \u00b7 IA \u00b7 Interaction \u00b7 Design system \u00b7 Visual design across 5 modules' },
                                { label: 'Timeline', value: '~3 months from discovery to shipped v1' },
                                { label: 'Domain', value: 'Enterprise SaaS \u00b7 Hybrid & multi-cloud (AWS \u00b7 Azure \u00b7 GCP)' },
                                { label: 'Users served', value: 'Cloud Architects \u00b7 Cloud Engineers \u00b7 IT Ops \u00b7 Business Managers' },
                            ],
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'The need',
                                    body: 'Complex, global cloud operations needed a scalable, usable interface, not another console.',
                                },
                                {
                                    title: 'The shift',
                                    body: 'A user-centric SaaS rebuild, flexible enough for enterprise, approachable enough for growing teams.',
                                },
                                {
                                    title: 'The advantage',
                                    body: 'Hybrid multi-cloud coverage, simplified workflows and clearer visibility across every business unit.',
                                },
                            ],
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 02 · RESEARCH & USERS
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Research & Users',
                    summary:
                        'A three-month mixed-methods study, interviews, benchmarks, card sorts and usability tests, anchored every decision.',
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'How we listened',
                            paragraph:
                                'A mixed-methods study over three months anchored every design decision. Interviews and analytics named the friction; card sorts re-derived the mental model; usability tests validated each shipped screen.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                { title: 'Interviews', body: 'Depth interviews across all four operator roles.' },
                                { title: 'Competitor benchmarks', body: 'Structured audits of directly-competing SaaS platforms.' },
                                { title: 'Card sorts & tree tests', body: 'To re-derive the resource hierarchy from operator language.' },
                                { title: 'Usability testing', body: 'Task-based tests on paper wireframes, mid-fi flows and shipped screens.' },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Two operators, one platform',
                            paragraph:
                                'Four roles use CloudHub. Two anchor personas captured the loudest tension: the admin who owns cost & security, and the engineer who ships infra fast.',
                        },
                        {
                            kind: 'cloudhub-journey-map',
                        },
                        {
                            kind: 'cloudhub-empathy-map',
                        },
                        {
                            kind: 'persona',
                            image: '/api/files/17c7b8e1-7ad9-4e0c-b517-5bd6598aa96a',
                            name: 'David, CloudHub Global Admin',
                            intro:
                                'Owns cost, security and compliance across a portfolio of programs. Wants a single pane of glass to spot risk and act on it.',
                            stats: [
                                { label: 'Age', value: '42' },
                                { label: 'Location', value: 'USA' },
                                { label: 'Role', value: 'Global Admin' },
                            ],
                            goals: [
                                'Single source of truth across AWS \u00b7 Azure \u00b7 GCP',
                                'Enforce tagging, quotas and approvals at scale',
                                'Cut monthly cloud spend without slowing teams',
                            ],
                            frustrations: [
                                'Data scattered across multiple consoles',
                                'Reports have to be stitched by hand each month',
                            ],
                        },
                        {
                            kind: 'persona',
                            image: '/api/files/fad83141-0104-4a72-9adf-29709eb60c93',
                            name: 'Varun, Cloud Engineer',
                            intro:
                                'Ships infra multiple times a day. Wants to move without waiting on tickets, and to see the impact of what he ships.',
                            stats: [
                                { label: 'Age', value: '30' },
                                { label: 'Location', value: 'India' },
                                { label: 'Role', value: 'Cloud Engineer' },
                            ],
                            goals: [
                                'Provision resources in minutes, not days',
                                'See utilisation and cost per workload',
                                'Reuse approved configurations across teams',
                            ],
                            frustrations: [
                                'Blocked on IT for every new server',
                                'Multi-step forms for routine changes',
                            ],
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 03 · APPROACH
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Approach',
                    summary:
                        'A five-phase Design Thinking loop tuned for a compressed enterprise timeline, each phase producing one artefact.',
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'Design Thinking, adapted for shipping',
                            paragraph:
                                'A five-phase Design Thinking loop, tuned to a compressed enterprise timeline. Each phase produced a specific artefact that unblocked the next, nothing existed for its own sake.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                { title: '01 \u00b7 Empathise', body: 'Interviews, journey maps, empathy maps.' },
                                { title: '02 \u00b7 Define', body: 'Personas, mental-model artefacts, IA hypotheses.' },
                                { title: '03 \u00b7 Ideate', body: 'Five module-level interventions, each mapped to a user task.' },
                                { title: '04 \u00b7 Prototype', body: 'Wireframes \u2192 token-driven design system \u2192 shipped-fidelity comps.' },
                                { title: '05 \u00b7 Deliver', body: 'Usability tests, engineering pairing, phased rollout.' },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Three guiding principles',
                            paragraph:
                                'If a proposal didn\u2019t serve at least one of these, it didn\u2019t ship.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'Internal \u2192 External',
                                    body: 'Turn an internal ops tool into a product external buyers can adopt without hand-holding.',
                                },
                                {
                                    title: 'One consistent experience',
                                    body: 'Every module speaks the same visual and interaction language.',
                                },
                                {
                                    title: 'Clarity from complexity',
                                    body: 'Cost, utilisation, tagging and state, visualised in decision-ready form.',
                                },
                            ],
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 04 · SOLUTION
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Solution',
                    summary:
                        'Structure before pixels, a re-derived mental model, and five modules built on one shared design system.',
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'Structure before pixels',
                            paragraph:
                                'Rough wireframes across every critical task were the first artefact. Once they passed usability tests, visual design followed. This kept every module rooted in behaviour rather than aesthetic preference.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/b2db7bbd-24ff-4c9a-af4c-bcaa732504a4',
                        },
                        {
                            kind: 'section',
                            heading: 'Re-deriving the mental model',
                            paragraph:
                                'Card sorts and tree tests re-derived the resource hierarchy into a four-level mental model: Account \u2192 Program \u2192 Environment \u2192 Server. Navigation, breadcrumbs, search and permissions all snapped to it.',
                        },
                        {
                            kind: 'cloudhub-ia',
                        },
                        {
                            kind: 'section',
                            heading: 'Five modules, one system',
                            paragraph:
                                'Each intervention shipped as production visual design, same tokens, same interaction primitives, five distinct problems solved.',
                        },
                        {
                            kind: 'sub-tabs',
                            heading: 'Visual design',
                            tabs: [
                                {
                                    label: 'Scheduler',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Scheduler Redesign',
                                            paragraph:
                                                'A program-level overview with region-aware filtering, guided creation, and clear status indicators. The old multi-step form became a single legible plane of activity.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/7f24a5da-8f35-49f3-975c-3e6875d6c11d',
                                        },
                                    ],
                                },
                                {
                                    label: 'Cloud Space',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Personalized Cloud Space',
                                            paragraph:
                                                'A customisable dashboard surfaces resources, server stats, tags and most-used actions. Engineers, admins and managers each get a different view of the same truth via draggable widgets and role-aware defaults.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/84ef64a1-6575-4908-9bd4-c39e72720c00',
                                        },
                                    ],
                                },
                                {
                                    label: 'Self-Service',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Self-Service Portal',
                                            paragraph:
                                                'Predefined configurations, approval workflows and real-time request tracking turned a multi-day IT dependency into a role-gated flow, every step auditable.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/65ea650d-bdda-4f40-9df0-fbeb5410932f',
                                        },
                                    ],
                                },
                                {
                                    label: 'Gamification',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Gamification Portal',
                                            paragraph:
                                                'Ratings, badges and team leaderboards rewarded efficient cloud usage, visibility and recognition are the levers that motivate ops teams.',
                                        },
                                        {
                                            kind: 'image',
                                            src: '/api/files/0b21d099-0439-4b74-9fe9-ed1abf9a45b0',
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Design system',
                            paragraph:
                                'A shared foundation of tokens, typography, colour, iconography, spacing, motion and interaction patterns. Every module inherits from the same primitives.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/db95b7b1-c487-460c-aa45-55151f63fffc',
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 05 · OUTCOMES & LEARNINGS
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Outcomes',
                    summary:
                        'Shipped responsive across desktop, tablet and mobile, with the lessons that outlived the project.',
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'Shipped everywhere operators work',
                            paragraph:
                                'CloudHub shipped as a fully responsive experience across desktop, tablet and mobile, one design system, five modules, three form-factors. Same primitives from a full-width monitor in the ops centre to a phone on-call.',
                        },
                        {
                            kind: 'image',
                            src: '/api/files/110ed493-ffa9-49bf-9db2-d4954aede6b9',
                        },
                        {
                            kind: 'cloudhub-impact',
                        },
                        {
                            kind: 'section',
                            heading: 'Lessons that outlived the project',
                            paragraph:
                                'A few things I still bring to every enterprise redesign, most of them are about process, not pixels.',
                        },
                        {
                            kind: 'bullets',
                            items: [
                                'Treat onboarding and empty-states as products, not afterthoughts.',
                                'Ship one primitive well before shipping five modules \u2014 the compounding pays for itself.',
                                'A weekly research beat keeps opinions from calcifying into decisions.',
                                'Design principles are useful only when they help you cut work.',
                                'Enterprise buyers care about outcomes; enterprise users care about their next task. Design for both.',
                            ],
                        },
                    ],
                },
            ],
        },
    ],
    next: { slug: 'symptom-tracking', title: 'Symptom Tracking' },
};
